import React, { useState, useMemo, useCallback } from 'react';
import PropTypes from 'prop-types';
import { useQuery, useMutation } from '@apollo/client';
import _ from 'lodash';

import { ErrorHandler, captureException } from '@playbookux/playbook-web-core';
import AudienceTemplate from '../../../components/template-audience';

import {
  GET_TEMPLATE_WITH_SURVEY_BY_TEMPLATE_ID,
  UPDATE_TEMPLATE,
} from '../../../../core/graphql/templates';

import {
  Container,
  ModalSave,
  ModalCancel,
} from './styles';

const EditAudience = (props) => {
  const {
    history,
    match: { params = {} },
  } = props;

  const [showModalCancel, setShowModalCancel] = useState(false);
  const [showModalSave, setShowModalSave] = useState(false);
  const [valuesSave, setValuesSave] = useState(null);
  const [templateError, setTemplateError] = useState(false);

  const { templateId } = params;

  const [
    updateTemplate,
    { loading: loadingUpdate, error: errorUpdate },
  ] = useMutation(UPDATE_TEMPLATE);

  const {
    data = {},
    error,
    loading,
  } = useQuery(GET_TEMPLATE_WITH_SURVEY_BY_TEMPLATE_ID, {
    variables: {
      templateId,
    },
  });

  const { template = {} } = data;

  const handleClose = useCallback(() => {
    history.push('/template-library#Audience');
  }, [history]);

  const handleShowModalSave = useCallback((values) => {
    if (values) {
      setValuesSave(values);
    }
    setShowModalSave((prev) => !prev);
    setTemplateError(false);
  }, [setValuesSave, setShowModalSave]);

  const handleShowModalCancel = useCallback(() => {
    setShowModalCancel((prev) => !prev);
    setTemplateError(false);
  }, [setShowModalCancel]);

  const templateValues = useMemo(() => {
    let audienceAttributes = [];
    let screenerQuestions = [];
    let screenerSurvey = {};

    if (!_.isEmpty(template.audienceAttributes)) {
      audienceAttributes = template.audienceAttributes.map(({ __typename, ...audienceAttribute }) => ({
        ...audienceAttribute,
        audienceAttributeOptions: audienceAttribute
          .audienceAttributeOptions.map(({ __typename: typename, ...audienceAttributeOption }) => ({ ...audienceAttributeOption })),
      }));
    }

    if (!_.isEmpty(template.survey)) {
      screenerSurvey = _.omit(template.survey, ['__typename']);

      screenerQuestions = _.chain(screenerSurvey.questions)
        .map((question) => {
          const value = _.omit(question, ['__typename']);

          const optionQuestions = _.map(question.questionOptions, (questionOption) => (
            _.omit(questionOption, ['__typename'])));

          return ({
            ...value,
            questionOptions: optionQuestions,
          });
        })
        .sortBy('questionOrder')
        .value();
    }

    return {
      language: template.language,
      testerRequirements: template.testerRequirements,
      questions: screenerQuestions,
      templateDescription: template.description,
      context: template.templateContext,
      templateName: template.name,
      device: template.device,
      operatingSystem: template.operatingSystem,
      surveyId: screenerSurvey.id,
      audienceAttributes,
    };
  }, [template]);

  const handleSubmit = useCallback(async () => {
    if (valuesSave) {
      try {
        await updateTemplate({
          variables: {
            id: templateId,
            attributes: valuesSave,
          },
        });

        handleClose();
      } catch (ex) {
        captureException('UPDATE_TEMPLATE_FAILED', ex);
        setTemplateError(true);
      }
    }
  }, [handleClose, templateId, updateTemplate, valuesSave]);

  if (error) {
    return (
      <Container center>
        <ErrorHandler
          error={error}
          defaultMessage="Error while retrieving templates."
        />
      </Container>
    );
  }

  return (
    <>
      <Container>
        <AudienceTemplate
          onCancel={handleShowModalCancel}
          title="Edit Saved Audience"
          onSave={handleShowModalSave}
          templateValues={templateValues}
          loading={loading}
        />
      </Container>

      <ModalCancel
        show={showModalCancel}
        onCancel={handleShowModalCancel}
        onConfirm={handleClose}
        isLoading={loading}
        textCancel="No, don't cancel"
        textConfirm="Yes, cancel"
        textContent="Do you want to cancel? Your updates will not be saved."
      />

      <ModalSave
        show={showModalSave}
        error={templateError ? errorUpdate : null}
        onCancel={handleShowModalSave}
        onConfirm={handleSubmit}
        isLoading={loadingUpdate}
        textCancel="Cancel"
        textConfirm="Save"
        textContent="Do you want to save your updates?"
      />

    </>
  );
};

EditAudience.propTypes = {
  history: PropTypes.shape({
    push: PropTypes.func,
  }).isRequired,
  match: PropTypes.shape({
    params: PropTypes.shape({
      templateId: PropTypes.string,
    }),
  }).isRequired,
};

export default EditAudience;
